import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { canonAPI } from '../lib/api';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Share2,
  Shield,
  Heart,
  Scale,
  Eye,
  RotateCcw,
  Copy,
  Check,
  Bird,
  GitBranch,
  Sun,
  AlertTriangle,
  MessageSquare,
  Twitter,
  Linkedin,
  Facebook
} from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useTranslation } from '../hooks/useTranslation';

const principleIcons = {
  dignity: Shield,
  liberty: Bird,
  'non-harm': Heart,
  proportionality: Scale,
  accountability: Eye,
  reversibility: RotateCcw,
  separation: GitBranch,
  transparency: Sun,
};

export default function ArticlePage() {
  const { articleNumber } = useParams();
  const { t } = useTranslation();
  const [article, setArticle] = useState(null);
  const [allArticles, setAllArticles] = useState([]);
  const [commentary, setCommentary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function loadArticle() {
      try {
        const [articleRes, allRes, commentaryRes] = await Promise.all([
          canonAPI.getArticle(parseInt(articleNumber)),
          canonAPI.getArticles(),
          canonAPI.getCommentary(parseInt(articleNumber)).catch(() => null),
        ]);
        setArticle(articleRes.data);
        setAllArticles(allRes.data);
        setCommentary(commentaryRes?.data || null);
      } catch (error) {
        console.error('Failed to load article:', error);
      } finally {
        setLoading(false);
      }
    }
    loadArticle();
  }, [articleNumber]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success(t('share.copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(t('share.failed'));
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Article ${article.number}: ${article.title}`,
          text: t('share.articleText', { number: article.number }),
          url: window.location.href,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink(); // This acts as a fallback if triggered directly
    }
  };

  const shareText = article ? t('share.articleText', { number: article.number }) : '';
  const shareUrl = window.location.href;

  const handleTwitterShare = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const handleLinkedInShare = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const handleFacebookShare = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8" data-testid="article-loading">
        <div className="loading-pulse h-64 rounded-lg" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="container mx-auto px-4 py-8 text-center" data-testid="article-not-found">
        <h2 className="text-2xl font-semibold mb-4">Article Not Found</h2>
        <Link to="/library">
          <Button>Back to Library</Button>
        </Link>
      </div>
    );
  }

  const currentIndex = allArticles.findIndex(a => a.number === article.number);
  const prevArticle = currentIndex > 0 ? allArticles[currentIndex - 1] : null;
  const nextArticle = currentIndex < allArticles.length - 1 ? allArticles[currentIndex + 1] : null;

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="article-page">
      {/* Breadcrumb */}
      <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-6">
        <Link to="/library" className="hover:text-foreground transition-colors flex items-center">
          <BookOpen className="h-4 w-4 mr-1" />
          Library
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span>{article.part_name}</span>
        <ChevronRight className="h-4 w-4" />
        <span className="text-foreground">Article {article.number}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <Card data-testid="article-content">
            <CardHeader>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Badge variant="outline">Article {article.number}</Badge>
                  {article.is_absolute && (
                    <Badge className="bg-amber-500/20 text-amber-700 dark:text-amber-400">
                      Absolute Right
                    </Badge>
                  )}
                </div>
                {navigator.share ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleShare}
                    data-testid="share-button"
                  >
                    {copied ? <Check className="h-4 w-4 mr-1" /> : <Share2 className="h-4 w-4 mr-1" />}
                    {t('share.button')}
                  </Button>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" data-testid="share-dropdown-trigger">
                        {copied ? <Check className="h-4 w-4 mr-1" /> : <Share2 className="h-4 w-4 mr-1" />}
                        {t('share.button')}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={handleCopyLink} className="cursor-pointer">
                        <Copy className="h-4 w-4 mr-2" />
                        {t('share.copyLink')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleTwitterShare} className="cursor-pointer">
                        <Twitter className="h-4 w-4 mr-2" />
                        {t('share.toX')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleLinkedInShare} className="cursor-pointer">
                        <Linkedin className="h-4 w-4 mr-2" />
                        {t('share.toLinkedIn')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleFacebookShare} className="cursor-pointer">
                        <Facebook className="h-4 w-4 mr-2" />
                        {t('share.toFacebook')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              <CardTitle className="text-2xl sm:text-3xl" style={{ fontFamily: 'var(--font-heading)' }}>
                {article.title}
              </CardTitle>
              <CardDescription>{article.part_name}</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="text" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="text">Canon Text</TabsTrigger>
                  <TabsTrigger value="commentary">Commentary</TabsTrigger>
                  {article.permitted_restrictions && (
                    <TabsTrigger value="restrictions">Restrictions</TabsTrigger>
                  )}
                </TabsList>

                <TabsContent value="text" className="space-y-4">
                  <div className="article-content text-lg leading-relaxed p-4 bg-muted/30 rounded-lg border-l-4 border-accent" data-testid="article-text">
                    {article.content}
                  </div>
                </TabsContent>

                <TabsContent value="commentary" className="space-y-4">
                  {commentary?.has_detailed_commentary ? (
                    <div className="space-y-6">
                      {/* Plain English Summary */}
                      <div className="p-4 rounded-lg bg-accent/10 border border-accent/30">
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <BookOpen className="h-4 w-4 text-accent" />
                          Plain English
                        </h4>
                        <p className="text-muted-foreground leading-relaxed">
                          {commentary.plain_english}
                        </p>
                      </div>

                      {/* Why It Matters */}
                      <div className="p-4 rounded-lg bg-muted/50">
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-500" />
                          Why It Matters
                        </h4>
                        <p className="text-muted-foreground leading-relaxed">
                          {commentary.why_it_matters}
                        </p>
                      </div>

                      {/* Key Safeguard */}
                      <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                        <h4 className="font-semibold mb-2 flex items-center gap-2">
                          <Shield className="h-4 w-4 text-emerald-500" />
                          Key Safeguard
                        </h4>
                        <p className="text-muted-foreground leading-relaxed">
                          {commentary.key_safeguard}
                        </p>
                      </div>

                      {/* What It Prevents */}
                      {commentary.what_it_prevents && (
                        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                            What It Prevents
                          </h4>
                          <ul className="space-y-2">
                            {commentary.what_it_prevents.map((item, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                <span className="text-red-500 mt-1">×</span>
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Pressure Test Questions */}
                      {commentary.pressure_test_questions && (
                        <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <Scale className="h-4 w-4 text-blue-500" />
                            Pressure Test Questions
                          </h4>
                          <ul className="space-y-2">
                            {commentary.pressure_test_questions.map((q, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                <span className="text-blue-500 font-bold">{idx + 1}.</span>
                                {q}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Cross References */}
                      {commentary.cross_references && (
                        <div className="p-4 rounded-lg bg-muted/30">
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <GitBranch className="h-4 w-4 text-accent" />
                            Cross-References
                          </h4>
                          <div className="space-y-2">
                            {commentary.cross_references.map((ref, idx) => (
                              <Link
                                key={idx}
                                to={`/article/${ref.article}`}
                                className="flex items-start gap-2 p-2 rounded hover:bg-muted transition-colors group"
                              >
                                <Badge variant="outline" className="group-hover:bg-accent/20">
                                  Art. {ref.article}
                                </Badge>
                                <span className="text-sm text-muted-foreground">{ref.reason}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Key Terms */}
                      {commentary.key_terms && (
                        <div className="flex flex-wrap gap-2">
                          <span className="text-sm text-muted-foreground mr-2">Key Terms:</span>
                          {commentary.key_terms.map((term, idx) => (
                            <Link key={idx} to={`/glossary?term=${encodeURIComponent(term)}`}>
                              <Badge variant="secondary" className="cursor-pointer hover:bg-accent/20">
                                {term}
                              </Badge>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="prose dark:prose-invert max-w-none">
                      <p className="text-muted-foreground leading-relaxed">
                        {article.commentary || commentary?.basic_commentary || "No commentary available for this article."}
                      </p>
                      {!commentary?.has_detailed_commentary && (
                        <p className="text-sm text-muted-foreground/70 mt-4 italic">
                          Detailed structured commentary for this article is coming soon.
                        </p>
                      )}
                    </div>
                  )}
                </TabsContent>

                {article.permitted_restrictions && (
                  <TabsContent value="restrictions" className="space-y-4">
                    <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                      <div className="flex items-start space-x-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                        <div>
                          <h4 className="font-medium mb-2">Permitted Restrictions</h4>
                          <p className="text-sm">{article.permitted_restrictions}</p>
                        </div>
                      </div>
                    </div>
                    {article.scope && (
                      <div className="p-4 rounded-lg bg-muted/50">
                        <h4 className="font-medium mb-2">Scope</h4>
                        <p className="text-sm text-muted-foreground">{article.scope}</p>
                      </div>
                    )}
                    {article.procedural_safeguards && (
                      <div className="p-4 rounded-lg bg-muted/50">
                        <h4 className="font-medium mb-2">Procedural Safeguards</h4>
                        <p className="text-sm text-muted-foreground">{article.procedural_safeguards}</p>
                      </div>
                    )}
                  </TabsContent>
                )}
              </Tabs>

              <Separator className="my-6" />

              {/* Metadata */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Part</span>
                  <p className="font-medium">{article.part_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Type</span>
                  <p className="font-medium">{article.is_absolute ? 'Absolute' : 'Qualified'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Version</span>
                  <p className="font-medium">1.0.0</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between mt-6">
            {prevArticle ? (
              <Link to={`/article/${prevArticle.number}`} data-testid="prev-article">
                <Button variant="outline">
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Art. {prevArticle.number}: {prevArticle.title.slice(0, 20)}...
                </Button>
              </Link>
            ) : <div />}
            {nextArticle && (
              <Link to={`/article/${nextArticle.number}`} data-testid="next-article">
                <Button variant="outline">
                  Art. {nextArticle.number}: {nextArticle.title.slice(0, 20)}...
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Related Principles */}
          <Card data-testid="related-principles">
            <CardHeader>
              <CardTitle className="text-lg">{t('article.relatedPrinciples')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {article.principle_tags?.map((tag) => {
                  const Icon = principleIcons[tag.toLowerCase()] || Shield;
                  return (
                    <Link
                      key={tag}
                      to={`/library?principle=${tag.toLowerCase()}`}
                      className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted transition-colors"
                      data-testid={`related-principle-${tag}`}
                    >
                      <Icon className="h-4 w-4 text-accent" />
                      <span className="capitalize font-medium">{t(`principles.${tag.toLowerCase()}`)}</span>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('article.quickActions')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link to={`/chat?context=article-${article.number}`} className="block">
                <Button variant="outline" className="w-full justify-start" data-testid="ask-about-article">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  {t('article.askAbout')}
                </Button>
              </Link>
              <Link to={`/four-scale`} className="block">
                <Button variant="outline" className="w-full justify-start" data-testid="four-scale-test">
                  <Scale className="h-4 w-4 mr-2" />
                  {t('article.fourScaleTest')}
                </Button>
              </Link>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleCopyLink}
                data-testid="copy-link"
              >
                <Copy className="h-4 w-4 mr-2" />
                {t('article.copyLink')}
              </Button>
            </CardContent>
          </Card>

          {/* Four-Scale Reminder */}
          {!article.is_absolute && (
            <Card className="border-accent/50 bg-accent/5">
              <CardContent className="pt-4">
                <div className="flex items-start space-x-2 text-sm">
                  <Scale className="h-4 w-4 text-accent mt-0.5" />
                  <div>
                    <p className="font-medium">{t('article.fourScaleRequired')}</p>
                    <p className="text-muted-foreground">
                      {t('article.restrictionDesc')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
